import { useEffect, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Layers, Plus, Save, Trash2 } from "lucide-react";
import { MODULE_LABELS } from "@/components/admin/ShopModulesEditor";
import { FEATURE_LABELS, FEATURE_PARENT, FEATURE_BLOCK_LABEL } from "@/lib/features";

function PlanCard({ plan, catalog, onSaved, onDeleted }) {
  const [f, setF] = useState({ ...plan, features: plan.features || [] });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const togMod = (k) => setF((p) => ({ ...p, modules: p.modules.includes(k) ? p.modules.filter((x) => x !== k) : [...p.modules, k] }));
  const togFeat = (k) => setF((p) => ({ ...p, features: p.features.includes(k) ? p.features.filter((x) => x !== k) : [...p.features, k] }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { name: f.name, price: Number(String(f.price).replace(",", ".")) || 0, modules: f.modules, features: f.features };
      const { data } = plan.id ? await api.put(`/admin/plans/${plan.id}`, body) : await api.post("/admin/plans", body);
      toast.success("Pacote salvo"); onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!plan.id) { onDeleted(); return; }
    if (!window.confirm(`Excluir o pacote ${plan.name}?`)) { return; }
    try { await api.delete(`/admin/plans/${plan.id}`); toast.success("Pacote excluído"); onDeleted(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const groups = {};
  for (const k of Object.keys(FEATURE_LABELS)) { (groups[FEATURE_PARENT[k]] ??= []).push(k); }
  void catalog;
  const tid = plan.id || "new";
  return (
    <div data-testid={`plan-card-${tid}`} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 space-y-2">
      <div className="flex gap-2">
        <Input data-testid={`plan-${tid}-name`} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nome" className="h-9 bg-zinc-900 border-zinc-800" />
        <Input data-testid={`plan-${tid}-price`} value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} inputMode="decimal" placeholder="Preço" className="h-9 w-28 bg-zinc-900 border-zinc-800" />
      </div>
      <p className="text-[11px] text-zinc-500">Sempre inclusos: OS, fotos e WhatsApp manual. Configurações nunca pode ser desligada.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
        {Object.entries(MODULE_LABELS).map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-xs text-zinc-300">
            <input type="checkbox" data-testid={`plan-${tid}-mod-${k}`} checked={f.modules.includes(k)} onChange={() => togMod(k)} className="h-3.5 w-3.5 accent-green-600" />{l}
          </label>
        ))}
      </div>
      <button type="button" data-testid={`plan-${tid}-features-toggle`} onClick={() => setOpen(!open)} className="text-[11px] font-bold text-green-300 underline">{open ? "Ocultar" : "Mostrar"} partes ({f.features.length})</button>
      {open && (
        <div className="space-y-2 border-t border-zinc-800 pt-2">
          <p className="text-[11px] text-zinc-500">Se nenhuma parte for marcada, o plano libera todas as partes dos módulos ligados.</p>
          {Object.entries(groups).map(([p, keys]) => (
            <div key={p} data-testid={`plan-${tid}-group-${p}`}>
              <div className="text-[11px] font-mono-caps text-green-300">{FEATURE_BLOCK_LABEL[p] || p} <span className="text-zinc-500">({keys.filter((k) => f.features.includes(k)).length}/{keys.length})</span></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-1">
                {keys.map((k) => (
                  <label key={k} className="flex items-center gap-2 text-xs text-zinc-300">
                    <input type="checkbox" data-testid={`plan-${tid}-feat-${k}`} checked={f.features.includes(k)} onChange={() => togFeat(k)} className="h-3.5 w-3.5 accent-green-600" />{FEATURE_LABELS[k]}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between gap-2 pt-1">
        <Button data-testid={`plan-${tid}-delete`} type="button" variant="outline" onClick={del} className="h-8 border-red-900 text-red-300"><Trash2 className="h-3.5 w-3.5" /></Button>
        <Button data-testid={`plan-${tid}-save`} type="button" onClick={save} disabled={busy} className="h-8 bg-green-brand hover:bg-green-brandDark text-white font-bold"><Save className="h-3.5 w-3.5 mr-1" /> Salvar</Button>
      </div>
    </div>
  );
}

export function PlansAdmin() {
  const [plans, setPlans] = useState([]);
  const load = () => api.get("/admin/plans").then(({ data }) => setPlans(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  return (
    <section data-testid="plans-admin" className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold uppercase text-zinc-100"><Layers className="h-5 w-5 text-green-neon" /> Planos (pacotes)</h2>
        <Button data-testid="plan-add" type="button" variant="outline" onClick={() => setPlans((p) => [...p, { id: null, name: "", price: "", modules: [], features: [] }])} className="h-8 border-zinc-700"><Plus className="h-4 w-4 mr-1" /> Novo pacote</Button>
      </div>
      <p className="text-xs text-zinc-500">Salvar atualiza módulos e partes das oficinas deste plano (exceções continuam).</p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {plans.map((p, i) => <PlanCard key={p.id || `n${i}`} plan={p} onSaved={() => load()} onDeleted={() => (p.id ? load() : setPlans((x) => x.filter((_, j) => j !== i)))} />)}
      </div>
      <p className="text-[11px] text-zinc-500">{plans.map((p) => `${p.name} ${brl(p.price)}`).join(" · ")}</p>
    </section>
  );
}
